export type CaseStudySectionType = 
  | "introduction"
  | "problem-statement" 
  | "goals-objectives"
  | "design-process"
  | "key-decisions"
  | "outcome-impact"
  | "learnings-reflections"
  | "conclusion"
  | "role-contribution"
  | "one-liner"
  | "custom";

export type ContentBlockType =
  | "text"
  | "image"
  | "video"
  | "gallery"
  | "carousel"
  | "line-break"
  | "quote"
  | "callout";

export type ElementPosition = 'inside' | 'below';

export interface CaseStudySection {
  id: string;
  type: CaseStudySectionType;
  order: number;
  title: string;
  content: string; // Rich text content (legacy)
  images: CaseStudyImage[];
  content_blocks?: ContentBlock[]; // New modular content blocks
  isCollapsed?: boolean; // New field for collapse state
  hideTitle?: boolean; // Hide title in preview (for element-only sections)
}

export interface CaseStudyImage {
  id: string;
  url: string;
  caption?: string;
  order: number;
}

export interface ContentBlock {
  id: string;
  type: ContentBlockType;
  content: Record<string, unknown>; // Type varies by block type
  order: number;
}

export type CaseStudyCreationMethod = "manual" | "imported";

export interface CaseStudyItem {
  id: string;
  title: string;
  description: string;
  cover_image: string;
  sections: CaseStudySection[];
  created_at: string;
  updated_at?: string;
  is_featured?: boolean;
  status?: "draft" | "published";
  order?: number;
  slug?: string;
  seo_title?: string;
  seo_description?: string;
  og_image?: string;
  creation_method: CaseStudyCreationMethod;
}

// Snapshot for undo/redo history (only tracks user-editable fields)
export interface CaseStudySnapshot {
  title: string;
  description: string;
  cover_image: string;
  sections: CaseStudySection[];
  is_featured: boolean;
  seo_title: string;
  seo_description: string;
  og_image: string;
  status: 'draft' | 'published';
  timestamp: number;
}

// History state for undo/redo functionality
export interface HistoryState {
  past: CaseStudySnapshot[];
  present: CaseStudySnapshot;
  future: CaseStudySnapshot[];
}

// Version types for permanent version history (database-persisted)
export type CaseStudyVersionType = 'manual' | 'auto_publish';

export interface CaseStudyVersion {
  id: string;
  case_study_id: string;
  version_number: number;
  label?: string;
  snapshot: CaseStudySnapshot;
  created_by: string;
  created_at: string;
  version_type: CaseStudyVersionType;
}

// Section configuration for UI
export const CASE_STUDY_SECTIONS = [
  {
    type: "introduction" as const,
    title: "Introduction",
    description: "Set the context and background for your project",
    icon: "BookOpen",
  },
  {
    type: "problem-statement" as const,
    title: "Problem Statement",
    description: "Define the challenge you were solving",
    icon: "Target",
  },
  {
    type: "goals-objectives" as const,
    title: "Goals & Objectives",
    description: "What you aimed to achieve",
    icon: "Target",
  },
  {
    type: "design-process" as const,
    title: "Design Process",
    description: "Your methodology and approach",
    icon: "RefreshCw",
  },
  {
    type: "key-decisions" as const,
    title: "Key Decisions & Trade-offs",
    description: "Important choices and their rationale",
    icon: "Scale",
  },
  {
    type: "outcome-impact" as const,
    title: "Outcome & Impact",
    description: "Results and measurable impact",
    icon: "TrendingUp",
  },
  {
    type: "learnings-reflections" as const,
    title: "Learnings & Reflections",
    description: "What you learned and would do differently",
    icon: "Lightbulb",
  },
  {
    type: "conclusion" as const,
    title: "Conclusion",
    description: "Wrap up and key takeaways",
    icon: "Flag",
  },
  {
    type: "role-contribution" as const,
    title: "Role & Contribution",
    description: "Your involvement and responsibilities",
    icon: "Users",
  },
  {
    type: "one-liner" as const,
    title: "One-liner Summary",
    description: "Brief summary of the project",
    icon: "Type",
  },
] as const;

// Project type for portfolio items
export type ProjectType = "shot" | "case-study";

// Content type definitions for sidebar
export const CONTENT_TYPES = [
  {
    type: "text" as const,
    title: "Text Section",
    description: "Add rich text content",
    icon: "Type",
    color: "bg-purple-100 text-purple-600",
    removable: true, // Can be deleted
  },
  {
    type: "image" as const,
    title: "Image, Video, GIF",
    description: "Upload media files",
    icon: "Image",
    color: "bg-green-100 text-green-600",
    removable: true, // Can be deleted
  },
  {
    type: "gallery" as const,
    title: "Image Gallery",
    description: "Create image galleries",
    icon: "Grid3X3",
    color: "bg-blue-100 text-blue-600",
    removable: true, // Can be deleted
  },
  {
    type: "carousel" as const,
    title: "Carousel",
    description: "Create image carousels",
    icon: "Images",
    color: "bg-indigo-100 text-indigo-600",
    removable: true, // Can be deleted
  },
  {
    type: "line-break" as const,
    title: "Line Break",
    description: "Add spacing",
    icon: "Minus",
    color: "bg-gray-100 text-gray-600",
    removable: true, // Can be deleted
  },
  {
    type: "quote" as const,
    title: "Quote Block",
    description: "Add styled quotes",
    icon: "Quote",
    color: "bg-orange-100 text-orange-600",
    removable: true, // Can be deleted
  },
  {
    type: "callout" as const,
    title: "Callout",
    description: "Add informational callouts",
    icon: "Info",
    color: "bg-blue-100 text-blue-600",
    removable: true, // Can be deleted
  },
] as const;

// Utility function to check if a content block type is removable
export function isContentBlockRemovable(blockType: ContentBlockType): boolean {
  const contentType = CONTENT_TYPES.find(ct => ct.type === blockType);
  return contentType?.removable ?? true; // Default to true if not specified
}

// Case study section templates for sidebar
export const CASE_STUDY_TEMPLATES = [
  {
    type: "introduction" as const,
    title: "Introduction",
    description: "Set the context and background",
    icon: "BookOpen",
  },
  {
    type: "goals-objectives" as const,
    title: "Goal / Objective",
    description: "Define project goals",
    icon: "Target",
  },
  {
    type: "design-process" as const,
    title: "Process Overview",
    description: "Your methodology",
    icon: "Settings",
  },
  {
    type: "role-contribution" as const,
    title: "Role & Contribution",
    description: "Your involvement",
    icon: "Users",
  },
  {
    type: "outcome-impact" as const,
    title: "Outcome / Impact",
    description: "Results achieved",
    icon: "BarChart3",
  },
  {
    type: "one-liner" as const,
    title: "One-liner Summary",
    description: "Brief summary",
    icon: "Type",
  },
  {
    type: "problem-statement" as const,
    title: "Context / Problem",
    description: "Problem definition",
    icon: "AlertTriangle",
  },
  {
    type: "key-decisions" as const,
    title: "Key Decisions & Trade-offs",
    description: "Important choices",
    icon: "Lightbulb",
  },
  {
    type: "learnings-reflections" as const,
    title: "What would you do differently?",
    description: "Reflections",
    icon: "RotateCcw",
  },
  {
    type: "conclusion" as const,
    title: "Conclusion",
    description: "Wrap up and key takeaways",
    icon: "Flag",
  },
] as const;


